import { zodResolver } from '@hookform/resolvers/zod';
import {
  ExperienceConfiguration,
  ExperienceConfigurationKeys,
  ExperienceConfigurationRequest,
  QuoteAndContractSettingsConfig,
  QuoteAndContractSettingsConfigWithKeySchema,
} from '@monetize/types/experience-config';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useGetExperienceConfiguration,
  useUpdateExperienceConfiguration,
} from '../../../../../api/experienceConfigurationService';
import {
  MAccordion,
  MAccordionCustomButton,
  MBox,
  MPageLoader,
} from '../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl, useQuoteSettings } from '../../../../../hooks';
import { useAsyncStatus } from '../../../../../hooks/useAsyncStatus';
import { useScrollToHighlightedSection } from '../../../../../hooks/useScrollToHighlightedSection';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { useSettingsSectionHighlights } from '../../../../../hooks/useSettingsSectionHighlights';
import { useFlags } from '../../../../../services/launchDarkly';
import { SettingsSection } from '../../../components/SettingsSection';
import { SettingsToggle } from '../../../components/SettingsToggle';
import {
  QUOTE_SETTING_KEYS,
  getQuoteSettingsSectionKeys,
  isQuoteSettingEnabled,
} from '../quoteSettingsConfig';
import {
  QuoteAndContractSettingsBooleanKeys,
  QuoteAndContractSettingsMetadata,
} from './QuoteExperienceConfigSettings/quote-settings-experience-config.utils';
import { QuoteTermsAndConditions } from './QuoteExperienceConfigSettings/QuoteTermsAndConditions';
import { ExperienceConfigRoleBasedSetting } from './QuoteExperienceConfigSettings/RoleBasedSetting';
import { quoteRateAndPriceSettingsOwnedBooleanKeys } from './QuoteRateAndPriceSettings';

export const QuoteAndContractExperienceConfiguration = () => {
  const sectionHighlight = useSettingsSectionHighlights(
    getQuoteSettingsSectionKeys(QUOTE_SETTING_KEYS.additionalSettings),
  );

  const { data, isLoading } = useGetExperienceConfiguration();
  const flags = useFlags();

  const { quoteSettings } = useQuoteSettings();
  const pauseAtAccepted = !!quoteSettings?.pauseAtAccepted;

  const { mutateAsync: doUpdateExperienceConfiguration, isPending } =
    useUpdateExperienceConfiguration({
      meta: { showErrorToast: true },
      onSuccess: (updatedData: ExperienceConfiguration) => {
        reset({
          key: ExperienceConfigurationKeys.quoteAndContractSettings,
          configuration:
            updatedData.configuration as QuoteAndContractSettingsConfig,
        });
      },
    });

  const { control, reset, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    resolver: zodResolver(QuoteAndContractSettingsConfigWithKeySchema),
    defaultValues: {
      key: ExperienceConfigurationKeys.quoteAndContractSettings,
      configuration: data?.quoteAndContractSettings || {},
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        key: ExperienceConfigurationKeys.quoteAndContractSettings,
        configuration:
          data.quoteAndContractSettings satisfies QuoteAndContractSettingsConfig,
      });
    }
  }, [data, reset]);

  useScrollToHighlightedSection(sectionHighlight, !!data && !isLoading);

  const onSubmit = async (formValues: ExperienceConfigurationRequest) => {
    await doUpdateExperienceConfiguration(formValues);
  };

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const { status, track } = useAsyncStatus();

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (Object.values(sectionHighlight).some(Boolean)) {
      setAccordionValue(['0']);
    }
  }, [sectionHighlight, setAccordionValue]);

  return (
    <MAccordion
      multiple
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
      data-testid="quote-and-contract-experience-config"
      w="100%"
    >
      <MSettingAccordionItem
        value="0"
        id={QUOTE_SETTING_KEYS.additionalSettings}
        {...getHighlightStyles(
          sectionHighlight[QUOTE_SETTING_KEYS.additionalSettings],
        )}
        renderAccordionButton={({ isExpanded }: { isExpanded: boolean }) => (
          <MAccordionCustomButton
            isExpanded={isExpanded}
            label="Additional Settings"
          />
        )}
        borderWidth="1px"
      >
        {data && !isLoading ? (
          <MBox mt={4}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {Object.entries(data.quoteAndContractSettings)
                .filter(
                  ([key, value]) =>
                    typeof value === 'boolean' &&
                    Object.keys(QuoteAndContractSettingsMetadata).includes(
                      key,
                    ) &&
                    !quoteRateAndPriceSettingsOwnedBooleanKeys.includes(
                      key as QuoteAndContractSettingsBooleanKeys,
                    ) &&
                    isQuoteSettingEnabled(
                      key as QuoteAndContractSettingsBooleanKeys,
                      flags,
                    ),
                )
                .map(([key]) => {
                  const typedKey = key as QuoteAndContractSettingsBooleanKeys;
                  const isHighlighted = !!sectionHighlight[typedKey];

                  return (
                    <SettingsSection
                      key={key}
                      id={typedKey}
                      {...getHighlightStyles(isHighlighted)}
                    >
                      <Controller
                        name={`configuration.${typedKey}`}
                        control={control}
                        render={({ field: { value, onChange, ...rest } }) => (
                          <SettingsToggle
                            heading={
                              QuoteAndContractSettingsMetadata[typedKey]?.label
                            }
                            subheading={
                              QuoteAndContractSettingsMetadata[typedKey]
                                ?.description
                            }
                            value={!!value}
                            asyncStatus={
                              activeKey === typedKey ? status : 'idle'
                            }
                            onChange={(newValue) => {
                              setActiveKey(typedKey);
                              onChange(newValue);
                              track(() => handleSubmit(onSubmit)());
                            }}
                            {...rest}
                          />
                        )}
                      />
                    </SettingsSection>
                  );
                })}

              <SettingsSection
                id="quoteCreateAllowedRoles"
                {...getHighlightStyles(
                  sectionHighlight['quoteCreateAllowedRoles'],
                )}
              >
                <ExperienceConfigRoleBasedSetting
                  metadataKey="quoteCreateAllowedRoles"
                  value={data.quoteAndContractSettings.quoteCreateAllowedRoles}
                  isLoading={isPending}
                  onSave={(newValue) => {
                    setValue('configuration.quoteCreateAllowedRoles', newValue);
                    handleSubmit(onSubmit)();
                  }}
                />
              </SettingsSection>

              <SettingsSection
                id="quoteNameEditableRoles"
                {...getHighlightStyles(
                  sectionHighlight['quoteNameEditableRoles'],
                )}
              >
                <ExperienceConfigRoleBasedSetting
                  metadataKey="quoteNameEditableRoles"
                  value={data.quoteAndContractSettings.quoteNameEditableRoles}
                  isLoading={isPending}
                  onSave={(newValue) => {
                    setValue('configuration.quoteNameEditableRoles', newValue);
                    handleSubmit(onSubmit)();
                  }}
                />
              </SettingsSection>

              {pauseAtAccepted && (
                <SettingsSection
                  id="quoteManualAcceptanceAllowedRoles"
                  {...getHighlightStyles(
                    sectionHighlight['quoteManualAcceptanceAllowedRoles'],
                  )}
                >
                  <ExperienceConfigRoleBasedSetting
                    metadataKey="quoteManualAcceptanceAllowedRoles"
                    value={
                      data.quoteAndContractSettings
                        .quoteManualAcceptanceAllowedRoles
                    }
                    isLoading={isPending}
                    onSave={(newValue) => {
                      setValue(
                        'configuration.quoteManualAcceptanceAllowedRoles',
                        newValue,
                      );
                      handleSubmit(onSubmit)();
                    }}
                  />
                </SettingsSection>
              )}

              <SettingsSection
                id="quoteAcceptanceAllowedRoles"
                {...getHighlightStyles(
                  sectionHighlight['quoteAcceptanceAllowedRoles'],
                )}
              >
                <ExperienceConfigRoleBasedSetting
                  metadataKey="quoteAcceptanceAllowedRoles"
                  value={
                    data.quoteAndContractSettings.quoteAcceptanceAllowedRoles
                  }
                  isLoading={isPending}
                  onSave={(newValue) => {
                    setValue(
                      'configuration.quoteAcceptanceAllowedRoles',
                      newValue,
                    );
                    handleSubmit(onSubmit)();
                  }}
                />
              </SettingsSection>

              <SettingsSection
                id="showTOCPermission"
                {...getHighlightStyles(sectionHighlight['showTOCPermission'])}
                showDivider={false}
              >
                <QuoteTermsAndConditions
                  value={data.quoteAndContractSettings.showTOCPermission}
                  onSave={(newValue) => {
                    setValue('configuration.showTOCPermission', newValue);
                    handleSubmit(onSubmit)();
                  }}
                />
              </SettingsSection>
            </form>
          </MBox>
        ) : (
          <MPageLoader />
        )}
      </MSettingAccordionItem>
    </MAccordion>
  );
};
